import { AwardingConfig, awardingConfigSchema } from '@procureai/schemas';
import { UseEditableWidgetBackend } from '@procureai/ui-modules-mui';

import { TsrctDemandAwardingRuleVariant } from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { AWARDING_CONFIGURATION_WIDGET_MUTATION as AWARDING_CONFIGURATION_WIDGET_MUTATION } from './AwardingConfigurationWidget.mutation';
import { AWARDING_CONFIGURATION_WIDGET_QUERY as AWARDING_CONFIGURATION_WIDGET_QUERY } from './AwardingConfigurationWidget.query';

const useAwardingConfigurationWidgetBackendInternal = createEditableWidgetGraphqlBackendHook({
  schema: awardingConfigSchema,
  query: AWARDING_CONFIGURATION_WIDGET_QUERY,
  mutation: AWARDING_CONFIGURATION_WIDGET_MUTATION,
});

export const useAwardingConfigurationWidgetBackend: UseEditableWidgetBackend<AwardingConfig> = ({ i18n }) => {
  const { entityId } = useModuleContext();
  const variables = { id: entityId };

  return useAwardingConfigurationWidgetBackendInternal({
    i18n,
    variables,
    skip: !entityId,
    toFormData: (data) => {
      const configuration = data.tesseract.demand.awardingConfiguration;
      return {
        automation: {
          enabled: configuration.performAutomaticAwarding,
        },
        // Stored rules may still use the PREFERRED_SUPPLIER variants the shared config dropped
        // (APP-6467). They load unchanged, and the form's validation blocks saving them.
        rules: configuration.rules.map((rule) => ({
          variant: rule.variant as AwardingConfig['rules'][number]['variant'],
        })),
      };
    },
    toMutationData: (formData, data) => {
      return {
        id: entityId,
        awardingConfiguration: {
          performAutomaticAwarding: formData.automation.enabled,
          // The shared awarding config no longer carries a deadline (APP-6467), but tesseract still
          // requires deadlineDays, so the stored value is sent back unchanged.
          deadlineDays: data?.tesseract.demand.awardingConfiguration.deadlineDays ?? 0,
          rules: formData.rules.map((rule) => ({
            variant: TsrctDemandAwardingRuleVariant[rule.variant],
            // TODO: price deviation support will be added in https://procureai.atlassian.net/browse/APP-3099
          })),
        },
      };
    },
    toSimpleFormWidgetProps(data, loading) {
      const deadline = data?.tesseract.demand.awardingConfiguration.deadline;
      return {
        deadline,
        disabled: loading || deadline,
      };
    },
  });
};
